/**
 * Frontend logger utility
 *
 * Provides structured logging for the client application with environment-aware
 * log level filtering and Sentry integration for error reporting.
 *
 * - Production: only warn and error are output
 * - Development: all levels (debug, info, warn, error) are output
 *
 * @module lib/logger
 */

import { Sentry } from '@/lib/sentry'
import { IS_PROD } from '@/lib/env'

/** Supported log severity levels */
type LogLevel = 'debug' | 'info' | 'warn' | 'error'

/** Numeric weight for each level used for filtering */
const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
}

/**
 * Minimum log level threshold.
 * In production only warn/error are emitted; in development everything is emitted.
 */
const MIN_LEVEL: LogLevel = IS_PROD ? 'warn' : 'debug'

/**
 * Returns true when the given level should be emitted based on the current
 * environment threshold.
 */
function shouldLog(level: LogLevel): boolean {
  return LOG_LEVEL_PRIORITY[level] >= LOG_LEVEL_PRIORITY[MIN_LEVEL]
}

/**
 * Formats a log prefix string.
 * When a module name is provided the output looks like `[module] message`,
 * otherwise the raw message is returned.
 */
function formatPrefix(module?: string): string {
  return module ? `[${module}]` : ''
}

/* eslint-disable no-console */

/**
 * Low-level write function that delegates to the appropriate console method.
 * This is the only place where console.* is called so that lint rules can
 * allowlist this single module.
 */
function write(level: LogLevel, prefix: string, message: string, ...args: unknown[]): void {
  const formattedMessage = prefix ? `${prefix} ${message}` : message

  switch (level) {
    case 'debug':
      console.debug(formattedMessage, ...args)
      break
    case 'info':
      console.info(formattedMessage, ...args)
      break
    case 'warn':
      console.warn(formattedMessage, ...args)
      break
    case 'error':
      console.error(formattedMessage, ...args)
      break
  }
}

/* eslint-enable no-console */

/**
 * Report an error to Sentry when available.
 * Captures the first Error argument if present, otherwise sends a plain message.
 */
function reportToSentry(message: string, args: unknown[]): void {
  try {
    const errorArg = args.find((arg): arg is Error => arg instanceof Error)

    if (errorArg) {
      Sentry.captureException(errorArg, {
        extra: { message },
      })
    } else {
      Sentry.captureMessage(message, 'error')
    }
  } catch {
    // Sentry may not be initialized -- silently ignore
  }
}

/**
 * Creates a scoped logger instance bound to a specific module name.
 * All messages emitted by the returned logger will be prefixed with the module.
 *
 * @param module - The module or context name (e.g. "WebSocket", "AuthService")
 * @returns A logger instance scoped to the given module
 */
function createLogger(module?: string) {
  const prefix = formatPrefix(module)

  return {
    /**
     * Log a debug-level message (suppressed in production)
     */
    debug(message: string, ...args: unknown[]): void {
      if (!shouldLog('debug')) return
      write('debug', prefix, message, ...args)
    },

    /**
     * Log an informational message (suppressed in production)
     */
    info(message: string, ...args: unknown[]): void {
      if (!shouldLog('info')) return
      write('info', prefix, message, ...args)
    },

    /**
     * Log a warning message
     */
    warn(message: string, ...args: unknown[]): void {
      if (!shouldLog('warn')) return
      write('warn', prefix, message, ...args)
    },

    /**
     * Log an error message and report to Sentry
     */
    error(message: string, ...args: unknown[]): void {
      if (!shouldLog('error')) return
      write('error', prefix, message, ...args)
      reportToSentry(prefix ? `${prefix} ${message}` : message, args)
    },
  }
}

/**
 * Default logger instance without a module prefix.
 * Use this for quick one-off logging or when a scoped logger is not needed.
 *
 * @example
 * ```ts
 * import { logger } from '@/lib/logger'
 *
 * logger.info('Application started')
 * logger.error('Something failed', error)
 * ```
 */
export const logger = createLogger()

/**
 * Factory for creating module-scoped loggers.
 *
 * @example
 * ```ts
 * import { createModuleLogger } from '@/lib/logger'
 *
 * const log = createModuleLogger('WebSocket')
 * log.info('Connected successfully') // => [WebSocket] Connected successfully
 * log.error('Connection timeout')    // => [WebSocket] Connection timeout (+ Sentry)
 * ```
 */
export { createLogger as createModuleLogger }
